import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  MAX_ADDED_SAMPLING_POINTS,
  parseAddedPointCount,
  tambahTitikOptionLabel,
  type SurveyTambahTitikOption,
} from './surveyTambahTitik';

type Props = {
  open: boolean;
  options: SurveyTambahTitikOption[];
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (payload: { samplingPlanDocumentId: string; addedPointCount: number }) => void;
};

export function SurveyTambahTitikDialog({
  open,
  options,
  submitting = false,
  error = null,
  onClose,
  onSubmit,
}: Props) {
  const [planId, setPlanId] = useState('');
  const [addedText, setAddedText] = useState('1');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPlanId(options[0]?.sampling_plan_document_id ?? '');
    setAddedText('1');
    setLocalError(null);
  }, [open, options]);

  const selected = options.find((opt) => opt.sampling_plan_document_id === planId) ?? null;
  const added = parseAddedPointCount(addedText);

  function handleSubmit() {
    if (!planId) {
      setLocalError('Pilih bidang pengujian.');
      return;
    }
    if (added == null) {
      setLocalError(`Jumlah titik harus bilangan bulat 1–${MAX_ADDED_SAMPLING_POINTS}.`);
      return;
    }
    setLocalError(null);
    onSubmit({ samplingPlanDocumentId: planId, addedPointCount: added });
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (submitting) return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
      data-testid="survey-tambah-titik-dialog"
    >
      <DialogTitle>Tambah titik sampling</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Pilih bidang pengujian yang akan ditambah titik. Titik baru langsung masuk berita acara,
            work order, dan quotation. Quotation akan masuk permintaan revisi agar marketing mereview.
          </Typography>
          <TextField
            select
            label="Bidang pengujian"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            fullWidth
            disabled={submitting || options.length === 0}
            inputProps={{ 'data-testid': 'survey-tambah-titik-bidang' }}
          >
            {options.map((opt) => (
              <MenuItem key={opt.sampling_plan_document_id} value={opt.sampling_plan_document_id}>
                {tambahTitikOptionLabel(opt)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Jumlah titik yang ditambah"
            type="number"
            value={addedText}
            onChange={(e) => setAddedText(e.target.value)}
            inputProps={{
              min: 1,
              max: MAX_ADDED_SAMPLING_POINTS,
              step: 1,
              'data-testid': 'survey-tambah-titik-count',
            }}
            disabled={submitting}
            fullWidth
          />
          {selected && added != null ? (
            <Typography variant="body2" color="text.secondary">
              {selected.bidang_pengujian_label}: {selected.current_point_count} →{' '}
              {selected.current_point_count + added} titik
            </Typography>
          ) : null}
          {localError || error ? (
            <Alert severity="error" data-testid="survey-tambah-titik-error">
              {localError || error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Batal
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || options.length === 0}
          data-testid="survey-tambah-titik-submit"
        >
          {submitting ? 'Menyimpan…' : 'Tambah titik'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
